/**
 * toolIntent
 * ---------------------------------------------------------------------------
 * Remembers which marketing entry page a visitor arrived from, so that after
 * sign-up they land in the tool they came for instead of the generic home.
 */
import { getToolLanding, type ToolSlug } from '@/config/toolLandings';

const KEY = 'psyforge_tool_intent';
/** Intent is a short-lived hint about *this* visit, not a lasting preference. */
const TTL_MS = 60 * 60 * 1000;

export function rememberToolIntent(slug: ToolSlug) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ slug, at: Date.now() }));
  } catch {
    /* storage unavailable — intent is a nicety, never a requirement */
  }
}

export function peekToolIntent(): ToolSlug | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    let slug: string | null = null;
    let at = 0;
    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw) as { slug?: string; at?: number };
      slug = parsed.slug ?? null;
      at = parsed.at ?? 0;
    }
    if (!slug || !at || Date.now() - at > TTL_MS) {
      clearToolIntent();
      return null;
    }
    return getToolLanding(slug)?.slug ?? null;
  } catch {
    return null;
  }
}


export function clearToolIntent() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Consume the stored intent and return where the new member should land.
 * Returns null when there is no intent, so callers keep their default.
 */
export function consumeToolIntentPath(): string | null {
  const slug = peekToolIntent();
  if (!slug) return null;
  clearToolIntent();
  return getToolLanding(slug)?.onboardingPath ?? null;
}

/** Sign-up link for a tool page, carrying the intent in the URL too. */
export function toolSignupPath(slug: ToolSlug): string {
  return `/auth?signup=1&intent=${slug}`;
}
